import { useCallback, useRef, useState } from "react";

import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, DialogContent, IconButton, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import styled from "styled-components";
import invariant from "ts-invariant";

import { usePostMessage } from "api/services/messaging/hooks";
import { MessagePayloadSchema } from "api/services/messaging/schemas/messaging";
import type { MessagePayload } from "api/services/messaging/schemas/messaging";
import { queryKeyPrefix } from "api/services/treatment/hooks/useGetPatientTreatmentScheduleWeek";
import { useProfileContext } from "contexts/ProfileContext/useProfileContext";
import { MessageInput } from "routes/messages/components/Conversation/MessageInput";
import { BaseButton } from "shared/atoms/BaseButton";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { MUIDialog } from "shared/atoms/MUI";
import { Notification } from "shared/atoms/Notification";
import { LeifGPT } from "shared/LeifGPT";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { reportError } from "utils/errorReporting";
import { useActivityLogging } from "utils/hooks/useActivityLogging";
import { useCurrentPatient } from "utils/hooks/useCurrentPatient";
import { isUsingActivityTracking } from "utils/patient";
import { userIsTherapist } from "utils/profile/profileHelper";

import type { ProtocolCard, SortableProtocolCard } from "./types";
import { isExercise } from "./utils";

interface CommentDialogProps {
  activity: ProtocolCard | SortableProtocolCard;
  smartReply: string | null | undefined;
  smartReplyId: number | null | undefined;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onPreview?: () => void;
}

export const CommentDialog: React.FC<CommentDialogProps> = ({
  activity,
  smartReply,
  smartReplyId,
  isOpen,
  setIsOpen,
  onPreview,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const { patient } = useCurrentPatient();
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const postMessage = usePostMessage();
  const { trackActivity } = useActivityLogging();
  const startTime = useRef(new Date().toISOString());

  invariant(profile, "Profile should be defined");
  const isTherapist = userIsTherapist(profile);

  const form = useForm<Omit<MessagePayload, "to">>({
    mode: "onSubmit",
    defaultValues: {
      activity_id: activity.user_activity_id ?? 0,
      body: "",
    },
    resolver: zodResolver(MessagePayloadSchema.omit({ to: true })),
  });
  const { isDirty } = form.formState;

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowConfirmClose(true);
    } else {
      setIsOpen(false);
      form.reset();
    }
  }, [form, isDirty, setIsOpen]);

  const handleSubmitComment = async (formData: MessagePayload) => {
    try {
      await postMessage.mutateAsync(formData, {
        onSuccess: () => {
          AnalyticsService.track(AnalyticsEvents.PATIENTS.COMMENT_REPLY, {
            patient: activity.patient_id,
          });
          if (isTherapist && activity.patient_id && isUsingActivityTracking(patient)) {
            trackActivity({
              category: "comment_posted",
              patient_id: patient.id,
              start_time: startTime.current,
              end_time: new Date().toISOString(),
            });
          }

          queryClient.invalidateQueries(queryKeyPrefix);
          setIsOpen(false);
          form.reset();
        },
      });
    } catch (error) {
      reportError("CommentDialog onSubmit error", new Error(JSON.stringify(error)));
    }
  };

  const onSubmit = form.handleSubmit(formData => {
    handleSubmitComment({ ...formData, to: activity.patient_id ?? 0 });
  });

  return (
    <>
      <MUIDialog
        dialogTitle={
          <>
            {activity.title}
            {onPreview && (
              <BaseButton
                variant="action"
                icon="eye"
                size="small"
                title={t("treatment.editor.preview_activity", {
                  activityType: isExercise(activity) ? "exercise" : "lesson",
                })}
                onClick={event => {
                  event.stopPropagation();
                  onPreview();
                }}
              />
            )}
            <IconButton onClick={handleClose} style={{ marginLeft: "auto" }}>
              <FontAwesomeIcon icon={faXmark} style={{ height: "24px", width: "24px" }} />
            </IconButton>
          </>
        }
        open={isOpen}
        onClose={handleClose}
        sx={{ "& .MuiDialog-paper": { overflow: "visible" } }}
      >
        <DialogContent sx={{ flex: "1 0 auto" }}>
          <Typography variant="subtitle2">{t("comments.patient_comment")}</Typography>
          <Typography variant="body1" whiteSpace="pre-wrap">
            {activity.comment}
          </Typography>

          {activity.reply && (
            <Box mt={2}>
              <Typography variant="subtitle2">{t("comments.your_reply")}</Typography>
              <Typography variant="body1" whiteSpace="pre-wrap">
                {activity.reply}
              </Typography>
            </Box>
          )}
        </DialogContent>

        {!activity.reply && (
          <>
            {smartReply && smartReplyId && activity.user_activity_id && activity.patient_id && (
              <StyledLeifGPT
                commentId={activity.user_activity_id}
                patientId={activity.patient_id}
                smartReply={smartReply}
                smartReplyId={smartReplyId}
                onEdit={value => {
                  form.setValue("body", value);
                  form.setFocus("body");
                }}
                onSubmit={value => {
                  if (activity.user_activity_id && activity.patient_id) {
                    handleSubmitComment({
                      activity_id: activity.user_activity_id,
                      body: value,
                      to: activity.patient_id,
                    });
                  }
                }}
                dismissEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_DISMISSED}
                editEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_EDITED}
                presentEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_PRESENTED}
                submitEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_SUBMITTED}
              />
            )}

            {postMessage.isError && (
              <Notification variant="danger" style={{ marginTop: "20px" }}>
                {t("errors.generic")}
              </Notification>
            )}

            <MessageInput
              form={form}
              onSubmit={onSubmit}
              placeholder={t("comments.your_reply")}
              slots={{ calendar: false, emoji: true, quickReplies: true }}
              error={!!form.formState.errors.body}
              helperText={!!form.formState.errors.body && t("errors.field.required")}
            />
          </>
        )}
      </MUIDialog>

      {showConfirmClose && (
        <ConfirmationPopup
          isOpen={showConfirmClose}
          title={t("comments.unsaved_reply")}
          body={t("comments.unsaved_reply_confirmation")}
          showCloseButton={false}
          onCancel={() => setShowConfirmClose(false)}
          onConfirm={() => {
            setShowConfirmClose(false);
            setIsOpen(false);
            form.reset();
          }}
        />
      )}
    </>
  );
};

const StyledLeifGPT = styled(LeifGPT)`
  width: calc(100% - 40px);
  margin: 0 auto 24px;
`;
